import React, { useEffect, useState } from "react";
import PartnerLayout, { tenantSlugFor } from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { KeyRound, Users, Award, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const HEALTH = {
  green: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-300", label: "Saludable" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/40", text: "text-amber-300", label: "En riesgo" },
  red: { bg: "bg-red-500/10", border: "border-red-500/40", text: "text-red-300", label: "Crítico" },
};

export default function PartnerDashboard() {
  const { user } = useAuth();
  const slug = tenantSlugFor(user);
  const [summary, setSummary] = useState(null);
  const [byProgram, setByProgram] = useState([]);

  useEffect(() => {
    api.get(`/partner/${slug}/summary`).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/partner/${slug}/reports/by-program`).then((r) => setByProgram(r.data)).catch(() => {});
  }, [slug]);

  const h = summary?.health_status ? HEALTH[summary.health_status] : HEALTH.amber;

  return (
    <PartnerLayout user={user}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Partner Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">{summary?.tenant?.name || "Cargando..."}</h1>
        <p className="mt-2 text-gray-400">Métricas de tu ecosistema en tiempo real.</p>

        <div className="mt-8 grid md:grid-cols-4 gap-4" data-testid="partner-kpis">
          <KPI icon={KeyRound} label="Activación" value={`${summary?.activation_rate ?? 0}%`} sub={`${summary?.licenses_activated ?? 0}/${summary?.licenses_total ?? 0}`} />
          <KPI icon={TrendingUp} label="Progreso promedio" value={`${summary?.avg_progress_percent ?? 0}%`} sub={`${summary?.enrollments_active ?? 0} activos`} />
          <KPI icon={Award} label="Completion" value={`${summary?.completion_rate ?? 0}%`} sub={`${summary?.certificates_issued ?? 0} certificados`} />
          <KPI icon={Users} label="Alumnos" value={summary?.alumnos_total ?? 0} sub={`${summary?.enrollments_total ?? 0} enrollments`} />
        </div>

        {summary && (
          <section className={`mt-8 p-6 rounded-2xl border ${h.border} ${h.bg}`} data-testid="health-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">Health Score</p>
                <p className={`mt-1 font-mono text-5xl font-bold ${h.text}`}>{summary.health_score}</p>
                <p className={`mt-1 text-sm ${h.text} font-semibold`}>{h.label}</p>
              </div>
              <div className="text-xs text-gray-400 max-w-md">
                Fórmula: activation_rate × 40 + avg_progress × 30 + completion_rate × 30. Umbrales: ≥70 verde, 40-69 amarillo, &lt;40 rojo.
              </div>
            </div>
          </section>
        )}

        {byProgram.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-semibold text-white mb-4">Progreso por programa</h2>
            <div className="p-4 bg-[#111827] border border-gray-800 rounded-xl h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byProgram}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
                  <XAxis dataKey="program_code" stroke="#9CA3AF" fontSize={11} />
                  <YAxis stroke="#9CA3AF" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1F2937", color: "#E5E7EB" }} />
                  <Bar dataKey="licenses_total" fill="#B84A0C" name="Licencias" />
                  <Bar dataKey="licenses_activated" fill="#3B82F6" name="Activadas" />
                  <Bar dataKey="certificates" fill="#10B981" name="Certificados" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </div>
    </PartnerLayout>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-5 bg-[#111827] border border-gray-800 rounded-xl">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-widest text-gray-500">{label}</p>
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <p className="mt-2 font-mono text-3xl font-bold text-white">{value}</p>
      <p className="mt-1 text-xs text-gray-400">{sub}</p>
    </div>
  );
}
